import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { GhostBtn } from 'common/components/molecules/ListToolbar';
import { UploadIcon, AlertTriangle } from 'common/images/FaIcons';
import ImportStepIndicator from './ImportStepIndicator';
import { bytesToMb, fileErrorMessage, isCsvFile } from './helpers';
import {
  Body, Muted, DropArea, DropTitle, Callout, Footer, FooterActions, LinkButton
} from './MachineCsv.styled';

function ImportUploadStep({
  checking, fileName, fileError, maxRows, maxBytes, editableFields, onFile, onOpenExport, onCancel
}) {
  const { t } = useTranslation();
  // A file refused here, before any upload: { code, params }.
  const [localError, setLocalError] = useState(null);
  const maxMb = bytesToMb(maxBytes);

  const handleDrop = useCallback((accepted, rejected) => {
    const file = accepted[0] || rejected[0];
    if (!file) return;
    if (!isCsvFile(file)) {
      setLocalError({ code: 'not_csv', params: {} });
      return;
    }
    if (file.size > maxBytes) {
      setLocalError({ code: 'file_too_large', params: { max_mb: maxMb } });
      return;
    }
    setLocalError(null);
    onFile(file);
  }, [onFile, maxBytes, maxMb]);

  const shownError = localError || fileError;
  const problem = shownError ? fileErrorMessage(t, shownError, { fields: editableFields }) : null;

  return (
    <Body>
      <Muted>
        {t('assets.csv.import.intro', {
          fields: editableFields,
          defaultValue: 'Upload a CSV exported from this page. Rows are matched on Machine ID. These columns can be changed: {{fields}}. Other columns are ignored.'
        })}
      </Muted>
      {problem && (
        <Callout tone="crit" role="alert">
          <AlertTriangle />
          <div>
            {fileName && !localError && <b>{fileName}: </b>}
            {problem}
          </div>
        </Callout>
      )}
      <DropArea accept=".csv,text/csv" multiple={false} onDrop={handleDrop} disabled={checking}>
        <UploadIcon />
        <DropTitle>
          {checking
            ? t('assets.csv.import.checking', { fileName, defaultValue: 'Checking {{fileName}}...' })
            : t('assets.csv.import.dropTitle', { defaultValue: 'Drop your CSV here, or browse' })}
        </DropTitle>
        <span>
          {t('assets.csv.import.dropLimits', {
            maxRows, maxMb, defaultValue: '.csv only, up to {{maxRows}} rows ({{maxMb}} MB)'
          })}
        </span>
      </DropArea>
      <Muted>
        {t('assets.csv.import.noFileYet', { defaultValue: 'No file yet?' })}
        {' '}
        <LinkButton type="button" onClick={onOpenExport}>
          {t('assets.csv.import.exportLink', { defaultValue: 'Export assets' })}
        </LinkButton>
        {' '}
        {t('assets.csv.import.exportLinkTail', { defaultValue: 'to get one with the right columns.' })}
      </Muted>
      <Footer>
        <ImportStepIndicator current="upload" />
        <FooterActions>
          <GhostBtn type="button" onClick={onCancel}>{t('common.cancel', { defaultValue: 'Cancel' })}</GhostBtn>
        </FooterActions>
      </Footer>
    </Body>
  );
}

ImportUploadStep.propTypes = {
  checking: PropTypes.bool.isRequired,
  fileName: PropTypes.string,
  fileError: PropTypes.shape({ code: PropTypes.string, params: PropTypes.object }),
  maxRows: PropTypes.number,
  maxBytes: PropTypes.number,
  editableFields: PropTypes.string,
  onFile: PropTypes.func.isRequired,
  onOpenExport: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

ImportUploadStep.defaultProps = {
  fileName: null,
  fileError: null,
  maxRows: 5000,
  maxBytes: 2 * 1024 * 1024,
  editableFields: 'Name, Unique ID, Description, Criticality'
};

export default ImportUploadStep;
